import { CloseIcon } from "@/domains/icons/huge-icons/monotone"
import { Button } from "@/components/button"

import type { ManagedSelectionDrawerViewProps } from "./types"
import { LockGridCardView } from "./lock-grid-card-view"
import { PowerMeterFooterView } from "./power-meter-footer-view"

export const ManagedSelectionDrawerView = ({
  activeLocks,
  inactiveLocks,
  selectedLockIds,
  totalPower,
  onToggleLock,
  onSelectAll,
  onDeselectAll,
  onClose,
}: ManagedSelectionDrawerViewProps) => {
  const selectedPower = [...activeLocks, ...inactiveLocks]
    .filter((lock) => selectedLockIds.includes(lock.id))
    .reduce((sum, lock) => sum + lock.lockValue, 0)

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-p500/90 backdrop-blur-sm duration-200 animate-in fade-in" onClick={onClose} />

      {/* Modal Container */}
      <div className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col overflow-hidden bg-p400 shadow-2xl duration-300 animate-in slide-in-from-bottom-4 sm:my-6 sm:rounded-2xl sm:border sm:border-p400-light">
        {/* Modal Header */}
        <div className="z-10 flex items-center justify-between border-b border-p400-light bg-p400 p-4 sm:p-6">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-t100">Select Power Source</h2>
            <p className="hidden text-sm text-t200 sm:block">
              Choose up to 20 NFTs to combine into your voting weight.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="percentage" size="sm" onClick={onSelectAll}>
              Select All
            </Button>
            <Button variant="percentage" size="sm" onClick={onDeselectAll}>
              Deselect All
            </Button>
            <button
              onClick={onClose}
              className="rounded-full bg-p300 p-2 text-t100 transition-colors hover:bg-p300-light"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>

        {/* Modal Grid Body - SCROLLABLE */}
        <div className="custom-scrollbar flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Active Locks Section (Can Vote) */}
          {activeLocks.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {activeLocks.map((lock) => (
                <LockGridCardView
                  key={lock.id}
                  lock={lock}
                  isSelected={selectedLockIds.includes(lock.id)}
                  onToggle={onToggleLock}
                />
              ))}
            </div>
          )}

          {/* Visual Separator - Only show if both sections have locks */}
          {activeLocks.length > 0 && inactiveLocks.length > 0 && (
            <div className="my-8 border-t border-p400-light" />
          )}

          {/* Inactive Locks Section (Cannot Vote - Expired or Voted) */}
          {inactiveLocks.length > 0 && (
            <>
              <h3 className="mb-4 text-base font-semibold text-t100">Unavailable</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                {inactiveLocks.map((lock) => (
                  <LockGridCardView
                    key={lock.id}
                    lock={lock}
                    isSelected={selectedLockIds.includes(lock.id)}
                    onToggle={onToggleLock}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <PowerMeterFooterView
          selectedPower={selectedPower}
          totalPower={totalPower}
          selectedCount={selectedLockIds.length}
          onConfirm={onClose}
        />
      </div>
    </div>
  )
}
